// 스테이지 보상 차등·스페셜 몬스터 공용 상수(정본: docs/STAGE-WORLD-PRD.md 3.2·4절). 데이터만 — 게임 연결은 S3(보상)·S4(스페셜) 레인.
// 수치는 가안이며 tools/sim 시뮬로 확정한 뒤 ECONOMY.md·economy.ts와 함께 고친다.
import { FINAL_BOSS_STAGE, MID_BOSS_STAGE } from './world';

export type StageKind = 'normal' | 'mid' | 'final';
export type DifficultyKey = 'normal' | 'hard' | 'hell';

/** 스테이지 번호(1..10) → 종류. n-5 중간보스, n-10 지역보스, 나머지 일반 */
export function stageKind(stage: number): StageKind {
  return stage === FINAL_BOSS_STAGE ? 'final' : stage === MID_BOSS_STAGE ? 'mid' : 'normal';
}

/**
 * 클리어 골드·카드 배율(PRD 3.2): 지역 클리어 base와 카드 골드에 곱한다(처치당 골드는 곱하지 않는다). 일반 0.95, n-5 2, n-10 3.5.
 * 일반 1 → 0.95: S3 시뮬(tools/sim/economy.ts MODEL_SHIPPED)에서 일반 1이면 엔드 구간 반복 수입이 일반만 돌 때보다 +7.1%로
 * PRD 한도 +6.5%를 넘어 PRD가 허용한 하한 0.95로 내렸다(0.95에서 최대 +6.3%, 첫 클리어 보상까지 더해도 +6.2%, docs/ECONOMY.md 'S3').
 */
export const CLEAR_REWARD_MULT: Readonly<Record<StageKind, number>> = { normal: 0.95, mid: 2, final: 3.5 };

/** 첫 클리어 보상(PRD 3.2): 골드 = 지역 클리어 base × gold, 강화석 개수, 확정 장비 최소 등급, 세트 조각 */
export interface FirstClearDef {
  readonly gold: number;
  readonly stones: number;
  readonly gearMinRarity: 'rare' | 'epic' | null;
  readonly setFragments: number;
}
export const FIRST_CLEAR: Readonly<Record<StageKind, FirstClearDef>> = {
  normal: { gold: 1, stones: 2, gearMinRarity: null, setFragments: 0 },
  mid: { gold: 3, stones: 5, gearMinRarity: 'rare', setFragments: 0 },
  final: { gold: 6, stones: 10, gearMinRarity: 'epic', setFragments: 1 },
};

export type SpecialKind = 'goldGoblin' | 'champion';

/** 한 판 스페셜 몬스터 출현 확률(PRD 4절). 보스판(n-5·n-10)은 0, 한 판 최대 1마리 */
export const SPECIAL_CHANCE: Readonly<Record<DifficultyKey, Readonly<Record<SpecialKind, number>>>> = {
  normal: { goldGoblin: 0.08, champion: 0.07 },
  hard: { goldGoblin: 0.1, champion: 0.1 },
  hell: { goldGoblin: 0.125, champion: 0.125 },
};

/** 스테이지·난이도에서 한 판 스페셜 출현 총확률(입장 카드 표시용) */
export function specialChance(stage: number, diff: DifficultyKey): number {
  if (stageKind(stage) !== 'normal') return 0;
  const c = SPECIAL_CHANCE[diff];
  return c.goldGoblin + c.champion;
}

/** 스페셜 몬스터 규칙(PRD 4절) */
export const SPECIAL = {
  goldGoblin: {
    /** 처음 맞은 뒤 도망치는 시간(초). 못 잡으면 땅속으로 사라진다 */
    fleeSec: 12,
    speedMult: 1.3,
    /** 처치 골드 = 지역 클리어 base × */
    gold: 1,
    stones: [1, 3] as const,
  },
  champion: {
    hpMult: 5,
    scale: 1.6,
    /** 처치 골드 = 지역 클리어 base × */
    gold: 0.5,
    /** 확정 장비 등급 가중을 한 단계 올린다 */
    gearRarityStep: 1,
  },
} as const;
